import { BaseApiService } from './BaseApiService';
import { WorkoutTemplateData, MuscleGroup, MovementUnit } from '../../types';

/**
 * Template Service
 * Handles all workout template-related API operations
 */
export class TemplateService extends BaseApiService {
  /**
   * GET /api/templates - Fetch all workout templates
   * @param params - Optional filters for muscle group, search, and type
   */
  async getTemplates(params?: {
    muscleGroup?: string;
    search?: string;
    type?: 'private' | 'public';
  }): Promise<WorkoutTemplateData[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.muscleGroup) {
      queryParams.append('muscleGroup', params.muscleGroup);
    }
    
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    
    if (params?.type) {
      queryParams.append('type', params.type);
    }
    
    const url = queryParams.toString() ? `/templates?${queryParams.toString()}` : '/templates';
    const result = await this.get<WorkoutTemplateData[]>(url);
    return result || [];
  }

  /**
   * GET /api/templates/:id - Get template by ID
   */
  async getTemplateById(id: string): Promise<WorkoutTemplateData | null> {
    return this.get<WorkoutTemplateData>(`/templates/${id}`);
  }

  /**
   * POST /api/templates - Create a new workout template
   */
  async createTemplate(data: {
    name: string;
    description?: string;
    muscleGroups: MuscleGroup[];
    exercises: Array<{
      exerciseId: string;
      sets: number;
      reps: number;
      weight: number;
      unit: MovementUnit;
    }>;
    userId?: string;
    isPrivate?: boolean;
  }): Promise<WorkoutTemplateData> {
    return this.post<WorkoutTemplateData>('/templates', data);
  }

  /**
   * PUT /api/templates/:id - Update an existing template
   */
  async updateTemplate(
    id: string,
    data: {
      name?: string;
      description?: string;
      muscleGroups?: MuscleGroup[];
      exercises?: Array<{
        exerciseId: string;
        sets: number;
        reps: number;
        weight: number;
        unit: MovementUnit;
      }>;
    }
  ): Promise<WorkoutTemplateData> {
    return this.put<WorkoutTemplateData>(`/templates/${id}`, data);
  }

  /**
   * DELETE /api/templates/:id - Delete a template
   */
  async deleteTemplate(id: string): Promise<void> {
    return this.delete<void>(`/templates/${id}`);
  }
}
